import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Loader, AlertCircle, Users, UserCheck } from 'lucide-react';
import Modal from '../Modal';
import EntityAssignPicker, { PickerItem } from './EntityAssignPicker';
import { secretariesApi } from '../../services/secretaryAssignmentService';
import { leadsApi } from '../../services/leadsService';
import clientsApi from '../../services/clientsService';

interface SecretaryRef {
  id: number;
  fullName: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  secretary: SecretaryRef | null;
  /** נקרא לאחר שמירה מוצלחת — לרענון/הודעת הצלחה בעמוד */
  onSaved?: (summary: string) => void;
}

const Wrapper = styled.div`
  direction: rtl;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const SectionTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary, #1f2937);
`;

const SectionCount = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
  color: #6c757d;
`;

const Divider = styled.div`
  height: 1px;
  background: #eef0f3;
`;

const StateRow = styled.div<{ $error?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: ${p => (p.$error ? '#dc2626' : '#6c757d')};
`;

const Spinner = styled(Loader)`
  animation: spin 0.9s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
`;

const CancelButton = styled.button`
  padding: 0.5rem 1rem;
  background: #fff;
  color: var(--text-primary, #1f2937);
  border: 1px solid var(--border-color, #dee2e6);
  border-radius: var(--border-radius-md, 6px);
  font-size: 0.9rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f1f3f5;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1.25rem;
  background: #0073ea;
  color: #fff;
  border: 1px solid #0073ea;
  border-radius: var(--border-radius-md, 6px);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #0060c2;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// --- searchFn-ים: חיפוש בשרת (עם debounce ברכיב ה-Picker) ---

const searchLeads = async (q: string): Promise<PickerItem[]> => {
  const res = await leadsApi.getAll({ searchTerm: q?.trim() || undefined, page: 1, pageSize: 50 });
  return res.leads.map(l => {
    const primary = l.contacts?.find(c => c.isPrimary) ?? l.contacts?.[0];
    return { id: l.id, label: l.fullName, sublabel: primary?.number };
  });
};

const searchClients = async (q: string): Promise<PickerItem[]> => {
  const res = await clientsApi.getAll({ searchTerm: q?.trim() || undefined, page: 1, pageSize: 50 });
  return res.clients.map(c => ({ id: c.id, label: c.fullName, sublabel: c.idNumber || c.phone || undefined }));
};

const extractError = (err: any, fallback: string): string => {
  if (err?.response?.status === 403) return 'אין לך הרשאה לבצע פעולה זו';
  const message = err?.response?.data?.message || err?.response?.data;
  return typeof message === 'string' ? message : fallback;
};

const SecretaryAssignmentsModal: React.FC<Props> = ({ isOpen, onClose, secretary, onSaved }) => {
  const [leads, setLeads] = useState<PickerItem[]>([]);
  const [clients, setClients] = useState<PickerItem[]>([]);
  const [initialLeadIds, setInitialLeadIds] = useState<Set<number>>(new Set());
  const [initialClientIds, setInitialClientIds] = useState<Set<number>>(new Set());

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (secretaryId: number) => {
    setLoading(true);
    setError(null);
    try {
      const [leadList, clientList] = await Promise.all([
        secretariesApi.getLeadsBySecretary(secretaryId),
        secretariesApi.getClientsBySecretary(secretaryId),
      ]);
      const leadItems: PickerItem[] = leadList.map(l => ({
        id: l.id, label: l.fullName, sublabel: l.phone || l.statusName,
      }));
      const clientItems: PickerItem[] = clientList.map(c => ({
        id: c.id, label: c.fullName, sublabel: c.idNumber || c.phone || c.statusName,
      }));
      setLeads(leadItems);
      setClients(clientItems);
      setInitialLeadIds(new Set(leadItems.map(i => i.id)));
      setInitialClientIds(new Set(clientItems.map(i => i.id)));
    } catch (err: any) {
      setError(extractError(err, 'שגיאה בטעינת השיוכים'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && secretary) {
      load(secretary.id);
    } else {
      // איפוס בסגירה
      setLeads([]);
      setClients([]);
      setInitialLeadIds(new Set());
      setInitialClientIds(new Set());
      setError(null);
      setSaving(false);
    }
  }, [isOpen, secretary, load]);

  const handleSave = async () => {
    if (!secretary) return;

    const currentLeadIds = leads.map(i => i.id);
    const currentClientIds = clients.map(i => i.id);
    const currentLeadIdSet = new Set(currentLeadIds);
    const currentClientIdSet = new Set(currentClientIds);

    const leadsToAdd = currentLeadIds.filter(id => !initialLeadIds.has(id));
    const leadsToRemove = Array.from(initialLeadIds).filter(id => !currentLeadIdSet.has(id));
    const clientsToAdd = currentClientIds.filter(id => !initialClientIds.has(id));
    const clientsToRemove = Array.from(initialClientIds).filter(id => !currentClientIdSet.has(id));

    const totalChanges =
      leadsToAdd.length + leadsToRemove.length + clientsToAdd.length + clientsToRemove.length;

    if (totalChanges === 0) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await Promise.all([
        ...leadsToAdd.map(id => secretariesApi.assignToLead(id, secretary.id)),
        ...leadsToRemove.map(id => secretariesApi.removeFromLead(id, secretary.id)),
        ...clientsToAdd.map(id => secretariesApi.assignToClient(id, secretary.id)),
        ...clientsToRemove.map(id => secretariesApi.removeFromClient(id, secretary.id)),
      ]);
      onSaved?.(`השיוכים של ${secretary.fullName} נשמרו בהצלחה`);
      onClose();
    } catch (err: any) {
      setError(extractError(err, 'שגיאה בשמירת השיוכים'));
    } finally {
      setSaving(false);
    }
  };

  if (!secretary) return null;

  const busy = loading || saving;

  return (
    <Modal
      isOpen={isOpen}
      onClose={busy ? () => {} : onClose}
      title={`שיוך לידים ולקוחות — ${secretary.fullName}`}
      width="640px"
      maxWidth="95vw"
      footer={
        <FooterRow>
          {saving && (
            <StateRow><Spinner size={14} /> שומר...</StateRow>
          )}
          <CancelButton onClick={onClose} disabled={busy}>ביטול</CancelButton>
          <SaveButton onClick={handleSave} disabled={busy}>
            {saving ? <Spinner size={14} /> : <UserCheck size={15} strokeWidth={2} />}
            שמור
          </SaveButton>
        </FooterRow>
      }
    >
      <Wrapper>
        {loading && (
          <StateRow><Spinner size={14} /> טוען שיוכים...</StateRow>
        )}
        {error && (
          <StateRow $error><AlertCircle size={14} /> {error}</StateRow>
        )}

        {!loading && (
          <>
            <Section>
              <SectionTitle>
                <Users size={16} strokeWidth={2} /> לידים משויכים
                <SectionCount>({leads.length})</SectionCount>
              </SectionTitle>
              <EntityAssignPicker
                selected={leads}
                onChange={setLeads}
                searchFn={searchLeads}
                placeholder="חפש ליד לפי שם..."
                disabled={busy}
                emptyText="לא משויכים לידים"
                noResultsText="לא נמצאו לידים תואמים"
              />
            </Section>

            <Divider />

            <Section>
              <SectionTitle>
                <Users size={16} strokeWidth={2} /> לקוחות משויכים
                <SectionCount>({clients.length})</SectionCount>
              </SectionTitle>
              <EntityAssignPicker
                selected={clients}
                onChange={setClients}
                searchFn={searchClients}
                placeholder="חפש לקוח לפי שם..."
                disabled={busy}
                emptyText="לא משויכים לקוחות"
                noResultsText="לא נמצאו לקוחות תואמים"
              />
            </Section>
          </>
        )}
      </Wrapper>
    </Modal>
  );
};

export default SecretaryAssignmentsModal;
